import type { PostureStatus } from '@/components/ui/posture-chip';
import { cn } from '@/lib/utils/cn';
import { ChartTooltip, useChartTooltip } from './chart-tooltip';
import {
  ChartEmpty,
  ChartLegend,
  POSTURE_BG,
  POSTURE_LABEL,
  POSTURE_WORST_FIRST,
  SrTable,
  cssVars,
} from './shared';
import { useEntryMotion } from './use-entry-motion';

/**
 * PostureUnitBar — one cell per domain, worst first (prototype `unitBar()`):
 * the program posture's population at a glance. Red, amber, green, then
 * the hatched not-rated cells; 2px apart, the ends rounded. Each cell names
 * its domain and rating in its tooltip and label; with `onSelect` the cells
 * are buttons that open the domain.
 *
 * PostureLegend sits under it: the counts per rating, from the API.
 */
export interface PostureUnit {
  id: string;
  name: string;
  posture: PostureStatus;
  /** Tooltip sub-line, e.g. "rated 7 Oct 2026 by Theo Kessler". */
  detail?: string | null;
}

export interface PostureUnitBarProps {
  units: PostureUnit[];
  onSelect?: (id: string) => void;
  /** Accessible name of the bar and its table. */
  label?: string;
  motionKey?: string | number;
  className?: string;
}

export function PostureUnitBar({
  units,
  onSelect,
  label = 'Domains by rating, worst first',
  motionKey,
  className,
}: PostureUnitBarProps) {
  const { animate, runKey } = useEntryMotion(motionKey);
  const { tip, bind } = useChartTooltip();

  if (!units.length) {
    return <ChartEmpty title="No domains yet" className={className} />;
  }

  // Ordering only: worst rating first, the given order within a rating.
  const sorted = units
    .map((u, i) => ({ u, i }))
    .sort(
      (a, b) =>
        POSTURE_WORST_FIRST.indexOf(a.u.posture) - POSTURE_WORST_FIRST.indexOf(b.u.posture) ||
        a.i - b.i,
    )
    .map(({ u }) => u);

  return (
    <div key={runKey} className={cn(animate && 'chart-anim', className)}>
      <div
        role="group"
        aria-label={label}
        className="mt-0.5 grid h-[30px] auto-cols-fr grid-flow-col gap-0.5"
      >
        {sorted.map((u, i) => {
          const word = POSTURE_LABEL[u.posture];
          const cell = cn(
            'a-pop block h-full border-0 p-0 first:rounded-l-[6px] last:rounded-r-[6px]',
            POSTURE_BG[u.posture],
            onSelect &&
              'cursor-pointer transition-[transform,filter] duration-[140ms] ease-out hover:-translate-y-0.5 hover:brightness-[1.12] focus-visible:-translate-y-0.5 focus-visible:brightness-[1.12] focus-visible:outline-none',
          );
          const handlers = bind({
            title: u.name,
            value: word,
            sub: u.detail ?? (u.posture === 'unassessed' ? 'not rated yet' : null),
          });
          return onSelect ? (
            <button
              key={u.id}
              type="button"
              className={cell}
              style={cssVars({ '--i': i })}
              aria-label={`${u.name}: ${word}`}
              onClick={() => onSelect(u.id)}
              {...handlers}
            />
          ) : (
            <span
              key={u.id}
              className={cell}
              style={cssVars({ '--i': i })}
              aria-hidden
              {...handlers}
            />
          );
        })}
      </div>
      <SrTable
        caption={label}
        head={['Domain', 'Rating']}
        rows={sorted.map((u) => [u.name, POSTURE_LABEL[u.posture]])}
      />
      <ChartTooltip tip={tip} />
    </div>
  );
}

const LEGEND_SWATCH: Record<PostureStatus, string> = {
  critical: 'bg-posture-critical',
  'at-risk': 'bg-posture-at-risk',
  healthy: 'bg-posture-healthy',
  unassessed: 'bg-ink-100',
};

/** "2 Red · 3 Amber · 4 Green · 1 Not rated": counts from the API, zero ratings left out. */
export function PostureLegend({
  counts,
  className,
}: {
  counts: Partial<Record<PostureStatus, number>>;
  className?: string;
}) {
  const keys = POSTURE_WORST_FIRST.filter((k) => (counts[k] ?? 0) > 0);
  return (
    <ChartLegend
      className={className}
      items={keys.map((k) => ({
        color: LEGEND_SWATCH[k],
        label: (
          <span>
            <b className="font-semibold text-ink-900">{counts[k]}</b> {POSTURE_LABEL[k]}
          </span>
        ),
      }))}
    />
  );
}
